import React, { useState, useEffect, useMemo } from 'react';
import {
  DollarSign,
  Coins,
  Calendar,
  User,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Clock,
  Edit2,
  Check,
  X,
  AlertCircle,
  AlertTriangle,
  Lock,
  Users,
  ShieldCheck,
  FileText
} from 'lucide-react';
import { UserRole } from '../types';
import {
  getCurrentPayPeriod,
  getAdjacentPayPeriod,
  getRecentPayPeriods,
  getPayPeriodForDate,
  formatDisplayDate,
  payrollCalendarDate,
  canFinalizePayPeriod,
  payrollAveragingApplies,
  AllegiantPayPeriod
} from '../utils/payPeriod';

interface PayrollViewProps {
  userRole: UserRole;
  onOpenAIWithContext: (prompt: string, contextId?: string) => void;
}

export const PayrollView: React.FC<PayrollViewProps> = ({ userRole, onOpenAIWithContext }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [expandedProviders, setExpandedProviders] = useState<Record<string, boolean>>({});
  const [editingAdjustmentId, setEditingAdjustmentId] = useState<string | null>(null);
  const [tempAdjustmentValue, setTempAdjustmentValue] = useState<string>('0');
  const [exceptionServiceId, setExceptionServiceId] = useState<string | null>(null);
  const [exceptionReason, setExceptionReason] = useState<string>('');
  const [editingRateProviderId, setEditingRateProviderId] = useState<string | null>(null);
  const [tempTreatmentRate, setTempTreatmentRate] = useState<string>('');
  const [tempEvalRate, setTempEvalRate] = useState<string>('');

  // Finalization state
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [finalizeNotes, setFinalizeNotes] = useState('');
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);
  const [finalizeResult, setFinalizeResult] = useState<{
    finalizedServicesCount: number;
    excludedServices: Array<{ id: string; clientName?: string; dos?: string; serviceCode?: string; calculatedPay?: number; clearanceLabel?: string; reasons?: string[] }>;
    carriedServices?: Array<{ id: string; clientName?: string; dos?: string; serviceCode?: string; calculatedPay?: number; clearanceLabel?: string }>;
  } | null>(null);

  // Canonical Allegiant Pay Period
  const [selectedPeriod, setSelectedPeriod] = useState<AllegiantPayPeriod>(() => getCurrentPayPeriod());
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [startDate, setStartDate] = useState(selectedPeriod.startDate);
  const [endDate, setEndDate] = useState(selectedPeriod.endDate);

  const recentPeriods = useMemo(() => getRecentPayPeriods(6, 4), []);

  const payrollToday = data?.payPeriod?.payrollToday || payrollCalendarDate();
  const averagingCutoff = payrollAveragingApplies(selectedPeriod.startDate);
  const isBeforeCutoff = !canFinalizePayPeriod(selectedPeriod, payrollToday);
  const prematureFinalizeMessage = averagingCutoff
    ? `Cannot finalize payroll before the Saturday after ${selectedPeriod.endDate}.`
    : `Cannot finalize payroll before Friday cutoff date (${selectedPeriod.endDate}).`;

  const handleSelectPeriod = (period: AllegiantPayPeriod) => {
    setSelectedPeriod(period);
    setStartDate(period.startDate);
    setEndDate(period.endDate);
    setIsCustomRange(false);
  };

  const handlePrevPeriod = () => {
    const prev = getAdjacentPayPeriod(selectedPeriod, -1);
    handleSelectPeriod(prev);
  };

  const handleNextPeriod = () => {
    const next = getAdjacentPayPeriod(selectedPeriod, 1);
    handleSelectPeriod(next);
  };

  const handleCurrentPeriod = () => {
    handleSelectPeriod(getCurrentPayPeriod());
  };

  const fetchPayroll = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/payroll?startDate=${startDate}&endDate=${endDate}`);
      if (res.ok) {
        const json = await res.json();
        setData(json);
        // Expand all by default
        const initialExpanded: Record<string, boolean> = {};
        json.providers?.forEach((p: any) => {
          initialExpanded[p.provider.id] = true;
        });
        setExpandedProviders(initialExpanded);
      }
    } catch (err) {
      console.error('Failed to fetch payroll:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayroll();
  }, [startDate, endDate]);

  const toggleExpand = (provId: string) => {
    setExpandedProviders(prev => ({ ...prev, [provId]: !prev[provId] }));
  };

  const handleSaveOwnerException = async (serviceId: string) => {
    const reason = exceptionReason.trim();
    if (!reason) {
      alert('A reason is required.');
      return;
    }
    try {
      const res = await fetch(`/api/payroll/owner-documentation-exception/${serviceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      if (res.ok) {
        setExceptionServiceId(null);
        setExceptionReason('');
        await fetchPayroll();
      } else {
        const errJson = await res.json();
        alert(errJson.error || 'Failed to save the owner documentation exception');
      }
    } catch (err) {
      console.error('Failed to save owner documentation exception:', err);
    }
  };

  const handleSaveAdjustment = async (serviceId: string) => {
    try {
      const res = await fetch(`/api/payroll/adjustments/${serviceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ manual_adjustment: Number(tempAdjustmentValue) || 0 })
      });
      if (res.ok) {
        setEditingAdjustmentId(null);
        await fetchPayroll();
      } else {
        const errJson = await res.json();
        alert(errJson.error || 'Failed to update adjustment');
      }
    } catch (err) {
      console.error('Failed to save adjustment:', err);
    }
  };

  const handleSaveRates = async (providerId: string) => {
    try {
      const tVal = tempTreatmentRate === '' ? null : Number(tempTreatmentRate);
      const eVal = tempEvalRate === '' ? null : Number(tempEvalRate);
      const res = await fetch(`/api/providers/${providerId}/rates`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rate_per_session: tVal,
          rate_per_eval: eVal
        })
      });
      if (res.ok) {
        setEditingRateProviderId(null);
        await fetchPayroll();
      } else {
        const errJson = await res.json();
        alert(errJson.error || 'Failed to save provider rates');
      }
    } catch (err) {
      console.error('Failed to save rates:', err);
    }
  };

  const handleFinalizePayroll = async () => {
    if (!data?.payPeriod?.id) return;
    setFinalizing(true);
    try {
      const res = await fetch('/api/payroll/finalize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payPeriodId: data.payPeriod.id,
          notes: finalizeNotes.trim() || undefined
        })
      });
      if (res.ok) {
        const result = await res.json();
        setFinalizeError(null);
        setFinalizeNotes('');
        setFinalizeResult({
          finalizedServicesCount: Number(result.finalizedServicesCount || 0),
          excludedServices: Array.isArray(result.excludedServices) ? result.excludedServices : [],
          carriedServices: Array.isArray(result.carriedServices) ? result.carriedServices : []
        });
        await fetchPayroll();
      } else {
        const errJson = await res.json();
        const message = errJson.error || 'Failed to finalize payroll.';
        setFinalizeError(message);
        alert(message);
      }
    } catch (err) {
      console.error('Failed to finalize payroll:', err);
      alert('An unexpected error occurred while finalizing payroll.');
    } finally {
      setFinalizing(false);
    }
  };

  const documentationExclusions = (data?.providers || []).flatMap((provider: any) =>
    (provider.services || [])
      .filter((service: any) => service.documentationEligible === false && !service.isLateException && !service.isFrozen)
      .map((service: any) => ({
        id: service.id,
        providerName: provider.provider?.name,
        clientName: service.clientName,
        dos: service.dos,
        serviceCode: service.serviceCode,
        calculatedPay: service.proratedPay,
        clearanceLabel: service.clearanceLabel,
        reasons: service.documentationHoldReason ? [service.documentationHoldReason] : []
      }))
  );
  const carriedInclusions = (data?.providers || []).flatMap((provider: any) =>
    (provider.services || [])
      .filter((service: any) => service.carriedFromEarlierPeriod && service.documentationEligible !== false)
      .map((service: any) => ({
        id: service.id,
        providerName: provider.provider?.name,
        clientName: service.clientName,
        dos: service.dos,
        serviceCode: service.serviceCode,
        calculatedPay: service.proratedPay,
        heldFromPeriodLabel: service.heldFromPeriodLabel
      }))
  );
  const totalCalculatedPayroll = data?.providers?.reduce((acc: number, p: any) => acc + (p.summary.calculatedTotalPay || 0), 0) || 0;
  const totalCompletedServices = data?.providers?.reduce((acc: number, p: any) => acc + (p.summary.completedServiceCount || 0), 0) || 0;
  const totalAdjustments = data?.providers?.reduce((acc: number, p: any) => acc + (p.summary.totalManualAdjustments || 0), 0) || 0;

  return (
    <div className="space-y-3 pb-12">
      {/* Mobile/Tablet Header Shell (xl:hidden) */}
      <div className="xl:hidden relative overflow-hidden rounded-2xl border mobile-header-shell sm:border-slate-700/80 sm:desktop-header-shell p-3.5 sm:p-4 space-y-2.5 sm:shadow-[0_4px_16px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.12)]">
        <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-sky-500/5 via-indigo-500/5 to-transparent pointer-events-none" />
        <div className="sm:hidden absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-transparent via-amber-400/40 to-transparent pointer-events-none" />
        <div className="hidden sm:block absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/30 to-transparent pointer-events-none" />

        {/* Left: Title & System Freeze Status */}
        <div className="relative z-10 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center space-x-2 shrink-0">
            <div className="w-6 h-6 rounded-lg bg-gradient-to-b from-amber-400/20 via-amber-500/15 to-amber-600/10 border border-amber-400/35 flex items-center justify-center text-amber-300 shadow-[0_0_10px_rgba(245,158,11,0.22),inset_0_1px_0_rgba(255,255,255,0.25)] shrink-0">
              <Coins className="w-3.5 h-3.5 text-amber-300 drop-shadow-[0_0_4px_rgba(245,158,11,0.4)]" />
            </div>
            <h1 className="text-base sm:text-lg font-bold text-white tracking-tight">Payroll</h1>
          </div>
          <span className="text-[9.5px] font-mono px-2 py-0.5 rounded-full bg-emerald-400/20 text-emerald-100 border border-emerald-300/40 font-semibold leading-none inline-flex items-center gap-1 shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
            <span>Authoritative Freeze Architecture</span>
          </span>
        </div>

        {/* Right: Canonical Allegiant Pay Period Command Rail */}
        <div className="relative z-10 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-1.5 justify-start w-full">
          {/* Main Selector & Period Navigation */}
          <div className="bg-[#3d5270]/90 px-2 py-1.5 sm:px-1.5 sm:py-1 rounded-xl border border-slate-400/40 text-xs w-full sm:w-auto min-w-0">
            {/* ROW 1: Previous navigation, calendar icon, and date selector */}
            <div className="flex items-center space-x-1 w-full min-w-0">
              <button
                onClick={handlePrevPeriod}
                title="Previous Pay Period"
                className="p-1 rounded-md hover:bg-white/5 text-slate-400 hover:text-white transition-colors cursor-pointer shrink-0"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>

              <div className="flex items-center space-x-1 px-1 flex-1 sm:flex-none min-w-0">
                <Calendar className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <select
                  value={selectedPeriod.id}
                  onChange={(e) => {
                    const target = recentPeriods.find(p => p.id === e.target.value);
                    if (target) handleSelectPeriod(target);
                  }}
                  className="bg-[#4a6080] border border-slate-400/50 rounded-md px-1.5 py-0.5 text-white font-mono text-[11px] focus:outline-none focus:border-emerald-400 cursor-pointer w-full sm:w-auto min-w-0"
                >
                  {recentPeriods.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={handleNextPeriod}
                title="Next Pay Period"
                className="p-1 rounded-md hover:bg-white/5 text-slate-400 hover:text-white transition-colors cursor-pointer shrink-0"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>

              {/* Desktop inline buttons */}
              <div className="hidden sm:flex items-center space-x-1 shrink-0">
                {!selectedPeriod.isCurrent && (
                  <button
                    onClick={handleCurrentPeriod}
                    className="px-1.5 py-0.5 rounded-md bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 font-semibold text-[9.5px] border border-emerald-500/30 transition-colors cursor-pointer"
                  >
                    Current
                  </button>
                )}

                <button
                  onClick={() => setIsCustomRange(!isCustomRange)}
                  className={`px-1.5 py-0.5 rounded-md text-[9.5px] font-semibold border transition-colors cursor-pointer ${
                    isCustomRange
                      ? 'bg-sky-500/20 text-sky-300 border-sky-500/40'
                      : 'bg-white/12 text-slate-100 border-white/25 hover:bg-white/18 hover:text-white'
                  }`}
                >
                  {isCustomRange ? 'Standard' : 'Custom'}
                </button>
              </div>
            </div>

            {/* ROW 2: Mobile-only Current Period indicator / Controls */}
            <div className="flex sm:hidden items-center justify-between pt-1 mt-1 border-t border-slate-800/80 min-w-0">
              {selectedPeriod.isCurrent ? (
                <div className="flex items-center space-x-1 px-1 text-[10px] font-semibold text-emerald-400">
                  <span>★</span>
                  <span>Current Period</span>
                </div>
              ) : (
                <button
                  onClick={handleCurrentPeriod}
                  className="px-2 py-0.5 rounded-md bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 font-semibold text-[10px] border border-emerald-500/30 transition-colors cursor-pointer flex items-center space-x-1"
                >
                  <span>★</span>
                  <span>Jump to Current Period</span>
                </button>
              )}

              <button
                onClick={() => setIsCustomRange(!isCustomRange)}
                className={`px-2 py-0.5 rounded-md text-[10px] font-semibold border transition-colors cursor-pointer shrink-0 ${
                  isCustomRange
                    ? 'bg-sky-500/20 text-sky-300 border-sky-500/40'
                    : 'bg-white/12 text-slate-100 border-white/25 hover:bg-white/18 hover:text-white'
                }`}
              >
                {isCustomRange ? 'Standard' : 'Custom'}
              </button>
            </div>
          </div>

          {/* Pay Period Timetable Badges (Cutoff / Process / Pay Date) */}
          <div className="flex flex-wrap items-center gap-1 text-[9.5px] font-mono text-slate-100 bg-[#3d5270]/90 px-1.5 py-1 rounded-xl border border-slate-400/40">
            <span className="px-1.5 py-0.5 rounded bg-white/10 border border-white/20">
              Cutoff: <strong className="text-amber-200">{formatDisplayDate(selectedPeriod.cutoffDate)} (Fri)</strong>
            </span>
            <span className="px-1.5 py-0.5 rounded bg-white/10 border border-white/20">
              Process: <strong className="text-sky-200">{formatDisplayDate(selectedPeriod.processingDate)} (Tue)</strong>
            </span>
            <span className="px-1.5 py-0.5 rounded bg-white/10 border border-white/20">
              Pay Date: <strong className="text-emerald-200">{formatDisplayDate(selectedPeriod.payDate)} (Wed)</strong>
            </span>
          </div>

          {/* Period Status & Finalize */}
          {data?.payPeriod?.status === 'FINALIZED' ? (
            <span className="px-2 py-1 rounded-xl bg-emerald-400/20 text-emerald-100 border border-emerald-300/40 flex items-center space-x-1 font-mono text-[9.5px] font-bold">
              <ShieldCheck className="w-3 h-3 text-emerald-400" />
              <span>FINALIZED {data.payPeriod.finalizedAt ? `(${new Date(data.payPeriod.finalizedAt).toLocaleDateString()})` : ''}</span>
            </span>
          ) : (
            <div className="flex items-center space-x-1.5">
              <span className="px-2 py-1 rounded-xl bg-sky-400/20 text-sky-100 border border-sky-300/45 flex items-center space-x-1 font-mono text-[9.5px] font-bold">
                <Clock className="w-3 h-3 text-sky-400" />
                <span>Open Period / Dynamic Tiers</span>
              </span>
              {(userRole === 'ADMIN' || userRole === 'OWNER') && !isCustomRange && (
                <button
                  onClick={() => {
                    if (isBeforeCutoff) {
                      alert(prematureFinalizeMessage);
                      return;
                    }
                    setFinalizeResult(null);
                    setFinalizeError(null);
                    setShowFinalizeModal(true);
                  }}
                  disabled={isBeforeCutoff}
                  className={`px-2.5 py-1 rounded-xl font-semibold text-[10px] transition-all flex items-center space-x-1 shadow-sm ${
                    isBeforeCutoff
                      ? 'bg-white/10 text-slate-300 border border-white/20 cursor-not-allowed'
                      : 'bg-gradient-to-b from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white cursor-pointer border border-emerald-400/40 shadow-emerald-900/20'
                  }`}
                  title={isBeforeCutoff ? prematureFinalizeMessage : 'Lock and finalize payroll for this pay period'}
                >
                  <ShieldCheck className="w-3 h-3" />
                  <span>Finalize Payroll</span>
                </button>
              )}
            </div>
          )}

          {/* Custom Date Override if toggled */}
          {isCustomRange && (
            <div className="flex items-center space-x-1.5 bg-white/10 px-2 py-1 rounded-xl border border-white/20 text-[10px]">
              <span className="text-slate-200">Custom:</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-[#4a6080] border border-slate-400/45 rounded px-1.5 py-0.5 text-white text-[10px] font-mono"
              />
              <span className="text-slate-300">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-[#4a6080] border border-slate-400/45 rounded px-1.5 py-0.5 text-white text-[10px] font-mono"
              />
            </div>
          )}
        </div>
      </div>

      {/* Desktop/Tablet Header Shell: Canonical rich navy / blue-grey shell */}
      <div className="hidden xl:block relative overflow-hidden rounded-2xl border border-slate-700/80 desktop-header-shell px-6 py-3.5 shadow-[0_4px_16px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.12)]">
        {/* Subtle specular top hairline */}
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/30 to-transparent pointer-events-none" />
        <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-sky-500/5 via-indigo-500/5 to-transparent pointer-events-none" />

        <div className="relative z-10 space-y-2.5">
          {/* TOP LINE: LEFT: Payroll, RIGHT: Authoritative Freeze Architecture */}
          <div className="flex items-center justify-between gap-4 w-full min-w-0">
            {/* Left: Title */}
            <div className="flex items-center space-x-2 shrink-0">
              <div className="w-6 h-6 rounded-lg bg-gradient-to-b from-amber-400/20 via-amber-500/15 to-amber-600/10 border border-amber-400/35 flex items-center justify-center text-amber-300 shadow-[0_0_10px_rgba(245,158,11,0.22),inset_0_1px_0_rgba(255,255,255,0.25)] shrink-0">
                <Coins className="w-3.5 h-3.5 text-amber-300 drop-shadow-[0_0_4px_rgba(245,158,11,0.4)]" />
              </div>
              <h1 className="text-base sm:text-lg font-bold text-white tracking-tight">Payroll</h1>
            </div>

            {/* Right: Authoritative Freeze Architecture */}
            <span className="text-[9.5px] font-mono px-2.5 py-1 rounded-full bg-emerald-400/20 text-emerald-100 border border-emerald-300/40 font-semibold leading-none inline-flex items-center gap-1.5 shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
              <span>Authoritative Freeze Architecture</span>
            </span>
          </div>

          {/* CENTER ROW 1 — PAY PERIOD */}
          <div className="flex items-center justify-center gap-2 flex-wrap w-full min-w-0">
            {/* Main Selector & Period Navigation */}
            <div className="bg-[#3d5270]/85 px-1.5 py-1 rounded-xl border border-slate-400/40 text-xs flex items-center space-x-1 shrink-0">
              <button
                onClick={handlePrevPeriod}
                title="Previous Pay Period"
                className="p-1 rounded-md hover:bg-white/5 text-slate-400 hover:text-white transition-colors cursor-pointer shrink-0"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>

              <div className="flex items-center space-x-1 px-1 shrink-0">
                <Calendar className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <select
                  value={selectedPeriod.id}
                  onChange={(e) => {
                    const target = recentPeriods.find(p => p.id === e.target.value);
                    if (target) handleSelectPeriod(target);
                  }}
                  className="bg-[#4a6080] border border-slate-400/50 rounded-md px-1.5 py-0.5 text-white font-mono text-[11px] focus:outline-none focus:border-emerald-400 cursor-pointer shrink-0"
                >
                  {recentPeriods.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={handleNextPeriod}
                title="Next Pay Period"
                className="p-1 rounded-md hover:bg-white/5 text-slate-400 hover:text-white transition-colors cursor-pointer shrink-0"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>

              {/* Inline current jump */}
              {!selectedPeriod.isCurrent && (
                <button
                  onClick={handleCurrentPeriod}
                  className="px-1.5 py-0.5 rounded-md bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 font-semibold text-[9.5px] border border-emerald-500/30 transition-colors cursor-pointer shrink-0"
                >
                  Current
                </button>
              )}

              <button
                onClick={() => setIsCustomRange(!isCustomRange)}
                className={`px-1.5 py-0.5 rounded-md text-[9.5px] font-semibold border transition-colors cursor-pointer shrink-0 ${
                  isCustomRange
                    ? 'bg-sky-500/20 text-sky-300 border-sky-500/40'
                    : 'bg-white/12 text-slate-100 border-white/25 hover:bg-white/18 hover:text-white'
                }`}
              >
                {isCustomRange ? 'Standard' : 'Custom'}
              </button>
            </div>

            {/* Custom Date Override if toggled */}
            {isCustomRange && (
              <div className="flex items-center space-x-1.5 bg-white/12 px-2 py-1 rounded-xl border border-white/25 text-[10px] shrink-0">
                <span className="text-slate-200">Custom:</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-[#4a6080] border border-slate-400/45 rounded px-1.5 py-0.5 text-white text-[10px] font-mono"
                />
                <span className="text-slate-300">to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-[#4a6080] border border-slate-400/45 rounded px-1.5 py-0.5 text-white text-[10px] font-mono"
                />
              </div>
            )}
          </div>

          {/* CENTER ROW 2 — PAYROLL TIMELINE */}
          <div className="flex items-center justify-center gap-2 flex-wrap w-full min-w-0 pt-1.5 border-t border-slate-800/60">
            {/* Pay Period Timetable Badges (Cutoff / Process / Pay Date) */}
            <div className="flex items-center gap-1.5 text-[9.5px] font-mono text-slate-100 bg-[#3d5270]/80 px-2 py-1 rounded-xl border border-slate-400/40 shrink-0 flex-wrap justify-center">
              <span className="px-1.5 py-0.5 rounded bg-white/12 border border-white/25 whitespace-nowrap">
                Cutoff: <strong className="text-amber-200">{formatDisplayDate(selectedPeriod.cutoffDate)} (Fri)</strong>
              </span>
              <span className="px-1.5 py-0.5 rounded bg-white/12 border border-white/25 whitespace-nowrap">
                Process: <strong className="text-sky-200">{formatDisplayDate(selectedPeriod.processingDate)} (Tue)</strong>
              </span>
              <span className="px-1.5 py-0.5 rounded bg-white/12 border border-white/25 whitespace-nowrap">
                Pay Date: <strong className="text-emerald-200">{formatDisplayDate(selectedPeriod.payDate)} (Wed)</strong>
              </span>
            </div>

            {/* Fourth item: Open Period / Dynamic Tiers (or Finalized status) */}
            {data?.payPeriod?.status === 'FINALIZED' ? (
              <span className="px-2.5 py-1 rounded-xl bg-emerald-400/20 text-emerald-100 border border-emerald-300/40 flex items-center space-x-1 font-mono text-[9.5px] font-bold whitespace-nowrap shrink-0">
                <ShieldCheck className="w-3 h-3 text-emerald-400" />
                <span>FINALIZED {data.payPeriod.finalizedAt ? `(${new Date(data.payPeriod.finalizedAt).toLocaleDateString()})` : ''}</span>
              </span>
            ) : (
              <div className="flex items-center space-x-1.5 shrink-0">
                <span className="px-2.5 py-1 rounded-xl bg-sky-400/20 text-sky-100 border border-sky-300/45 flex items-center space-x-1 font-mono text-[9.5px] font-bold whitespace-nowrap shrink-0">
                  <Clock className="w-3 h-3 text-sky-400" />
                  <span>Open Period / Dynamic Tiers</span>
                </span>
                {(userRole === 'ADMIN' || userRole === 'OWNER') && !isCustomRange && (
                  <button
                    onClick={() => {
                      if (isBeforeCutoff) {
                        alert(prematureFinalizeMessage);
                        return;
                      }
                      setFinalizeResult(null);
                    setFinalizeError(null);
                    setShowFinalizeModal(true);
                    }}
                    disabled={isBeforeCutoff}
                    className={`px-2.5 py-1 rounded-xl font-semibold text-[10px] transition-all flex items-center space-x-1 shadow-sm whitespace-nowrap shrink-0 ${
                      isBeforeCutoff
                        ? 'bg-white/10 text-slate-300 border border-white/20 cursor-not-allowed'
                        : 'bg-gradient-to-b from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white cursor-pointer border border-emerald-400/40 shadow-emerald-900/20'
                    }`}
                    title={isBeforeCutoff ? prematureFinalizeMessage : 'Lock and finalize payroll for this pay period'}
                  >
                    <ShieldCheck className="w-3 h-3" />
                    <span>Finalize Payroll</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Late Service Exceptions Alert Banner */}
      {data?.payPeriod?.hasLateExceptions && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 sm:p-4 flex items-start space-x-3 text-amber-300 shadow-sm">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-xs space-y-1 flex-1">
            <div className="font-bold text-amber-200 flex items-center space-x-2">
              <span>Late Encounter Exceptions Detected ({data.payPeriod.lateExceptionsCount})</span>
              <span className="text-[9.5px] uppercase font-mono px-2 py-0.5 rounded bg-amber-950/80 border border-amber-500/30 text-amber-300">
                Admin Review Required
              </span>
            </div>
            <p className="text-amber-300/90 leading-relaxed text-[10.5px]">
              Encounters rendered in this finalized period but entered after payroll freeze. Flagged as Late Service — Admin Payroll Review Required.
            </p>
            <div className="pt-0.5 flex flex-wrap gap-1.5">
              {data.payPeriod.lateExceptions?.map((ex: any) => (
                <span key={ex.serviceId} className="px-2 py-0.5 rounded bg-amber-950/80 border border-amber-500/40 text-[10px] font-mono text-amber-200">
                  {ex.providerName} • {ex.clientName} ({ex.dos}) • CPT {ex.serviceCode}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Aggregate Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
        {/* Total Providers (Cyan/Blue) */}
        <div className="relative overflow-hidden rounded-xl border border-slate-700/80 md:border-slate-700/70 mobile-ops-kdi md:from-[#182436] md:to-[#101826] px-3.5 py-2.5 shadow-[0_3px_12px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.18)] md:shadow-[0_4px_16px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.08)] transition-all">
          <div className="absolute inset-x-0 top-0 h-[1.5px] bg-gradient-to-r from-transparent via-sky-400/40 to-transparent pointer-events-none" />
          <div className="flex items-center justify-between text-slate-600 text-xs font-medium">
            <span className="text-[11px] font-semibold text-slate-700">Total Providers</span>
            <div className="p-1 rounded-md bg-sky-500/10 text-sky-400 border border-sky-500/20">
              <Users className="w-3.5 h-3.5 text-sky-400" />
            </div>
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="text-xl font-bold text-slate-900 font-mono tracking-tight">
              {data?.providers?.length || 0}
            </span>
            <span className="text-[10px] text-slate-600 font-medium">Active therapy staff</span>
          </div>
        </div>

        {/* Completed Services (Green) */}
        <div className="relative overflow-hidden rounded-xl border border-slate-700/80 md:border-slate-700/70 mobile-ops-kdi md:from-[#182436] md:to-[#101826] px-3.5 py-2.5 shadow-[0_3px_12px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.18)] md:shadow-[0_4px_16px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.08)] transition-all">
          <div className="absolute inset-x-0 top-0 h-[1.5px] bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent pointer-events-none" />
          <div className="flex items-center justify-between text-slate-600 text-xs font-medium">
            <span className="text-[11px] font-semibold text-slate-700">Completed Services</span>
            <div className="p-1 rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              <Check className="w-3.5 h-3.5 text-emerald-400" />
            </div>
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="text-xl font-bold text-emerald-800 font-mono tracking-tight">
              {totalCompletedServices}
            </span>
            <span className="text-[10px] text-slate-600 font-medium">Rendered in pay period</span>
          </div>
        </div>

        {/* Manual Adjustments (Amber) */}
        <div className="relative overflow-hidden rounded-xl border border-slate-700/80 md:border-slate-700/70 mobile-ops-kdi md:from-[#182436] md:to-[#101826] px-3.5 py-2.5 shadow-[0_3px_12px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.18)] md:shadow-[0_4px_16px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.08)] transition-all">
          <div className="absolute inset-x-0 top-0 h-[1.5px] bg-gradient-to-r from-transparent via-amber-400/40 to-transparent pointer-events-none" />
          <div className="flex items-center justify-between text-slate-600 text-xs font-medium">
            <span className="text-[11px] font-semibold text-slate-700">Manual Adjustments</span>
            <div className="p-1 rounded-md bg-amber-500/10 text-amber-400 border border-amber-500/20">
              <Edit2 className="w-3.5 h-3.5 text-amber-400" />
            </div>
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="text-xl font-bold text-amber-800 font-mono tracking-tight">
              ${totalAdjustments.toFixed(2)}
            </span>
            <span className="text-[10px] text-slate-600 font-medium">Direct staff adjustments</span>
          </div>
        </div>

        {/* Calculated Gross Pay (Emerald) */}
        <div className="relative overflow-hidden rounded-xl border border-slate-700/80 md:border-slate-700/70 mobile-ops-kdi md:from-[#182436] md:to-[#101826] px-3.5 py-2.5 shadow-[0_3px_12px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.18)] md:shadow-[0_4px_16px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.08)] transition-all">
          <div className="absolute inset-x-0 top-0 h-[1.5px] bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent pointer-events-none" />
          <div className="flex items-center justify-between text-slate-600 text-xs font-medium">
            <span className="text-[11px] font-semibold text-emerald-800">Calculated Gross Pay</span>
            <div className="p-1 rounded-md bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
              <DollarSign className="w-3.5 h-3.5 text-emerald-400" />
            </div>
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="text-xl font-bold text-emerald-800 font-mono tracking-tight">
              ${totalCalculatedPayroll.toFixed(2)}
            </span>
            <span className="text-[10px] text-emerald-700 font-medium">
              {data?.payPeriod?.status === 'FINALIZED' ? 'Frozen gross pay' : 'Dynamic open pay'}
            </span>
          </div>
        </div>
      </div>

      {/* Provider Payroll List */}
      {loading ? (
        <div className="glass-panel py-8 px-4 rounded-xl text-center text-slate-600 text-xs border border-slate-200">
          Calculating payroll from completed services...
        </div>
      ) : !data?.providers || data.providers.length === 0 ? (
        <div className="glass-panel py-8 px-4 rounded-xl text-center text-slate-600 text-xs border border-slate-200">
          No provider payroll records available for this period.
        </div>
      ) : (
        <div className="space-y-2.5">
          {data.providers.map((item: any) => {
            const { provider, summary, services } = item;
            const isExpanded = !!expandedProviders[provider.id];

            return (
              <div
                key={provider.id}
                className="relative border border-slate-200 hover:border-slate-300 rounded-xl overflow-hidden bg-white transition-colors shadow-xs"
              >

                {/* Provider Payroll Row Header */}
                <div
                  onClick={() => toggleExpand(provider.id)}
                  className="p-2 sm:px-3 sm:py-2 bg-gradient-to-b from-[#20314c] to-[#18263d] border-b border-slate-700/80 flex flex-col xl:flex-row xl:items-center justify-between gap-2 hover:bg-[#253958] transition-colors cursor-pointer select-none"
                >
                  <div className="flex items-center space-x-2.5 min-w-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpand(provider.id);
                      }}
                      className="p-1 rounded-md bg-white/12 hover:bg-white/20 text-white border border-white/25 text-xs transition-colors cursor-pointer shrink-0"
                      title={isExpanded ? 'Collapse completed services' : 'Expand completed services'}
                    >
                      {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    </button>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 leading-tight">
                        <span className="font-bold text-white text-xs sm:text-sm tracking-tight">{provider.name}</span>
                        {provider.title && (
                          <span className="text-[11px] text-slate-400 hidden sm:inline">
                            • {provider.title}
                          </span>
                        )}
                        <span className="text-[9.5px] font-mono px-1.5 py-0.5 rounded bg-sky-400/20 text-sky-100 border border-sky-300/40 font-semibold leading-none">
                          {provider.role}
                        </span>
                        {provider.supervisorName && (
                          <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-indigo-400/20 text-indigo-100 border border-indigo-300/40 leading-none">
                            Supervised by {provider.supervisorName}
                          </span>
                        )}
                        {summary.lateExceptionsCount > 0 && (
                          <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 font-semibold flex items-center space-x-1 leading-none shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
                            <AlertTriangle className="w-3 h-3 text-amber-400" />
                            <span>{summary.lateExceptionsCount} Late</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Provider Key Stats & Compensation Source */}
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-xs shrink-0 self-end xl:self-center"
                  >
                    {/* Rate Editor Modal / Popover */}
                    {editingRateProviderId === provider.id ? (
                      <div className="flex items-center space-x-1.5 bg-[#3d5270] p-1.5 rounded-lg border border-sky-300/40 text-xs">
                        <div className="flex flex-col space-y-0.5">
                          <label className="text-[9.5px] text-slate-400">Tx ($/sess):</label>
                          <input
                            type="number"
                            value={tempTreatmentRate}
                            onChange={(e) => setTempTreatmentRate(e.target.value)}
                            placeholder="0 = ladder"
                            className="w-20 bg-[#4a6080] border border-slate-400/45 text-white px-1.5 py-0.5 rounded text-xs font-mono"
                          />
                        </div>
                        <div className="flex flex-col space-y-0.5">
                          <label className="text-[9.5px] text-slate-400">Eval ($/eval):</label>
                          <input
                            type="number"
                            value={tempEvalRate}
                            onChange={(e) => setTempEvalRate(e.target.value)}
                            placeholder="0 = $175/$150"
                            className="w-20 bg-[#4a6080] border border-slate-400/45 text-white px-1.5 py-0.5 rounded text-xs font-mono"
                          />
                        </div>
                        <div className="flex items-center space-x-1 pt-2.5">
                          <button
                            onClick={() => handleSaveRates(provider.id)}
                            className="p-1 text-emerald-400 hover:text-emerald-300 active:scale-95 cursor-pointer transition-transform"
                            title="Save custom rates"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setEditingRateProviderId(null)}
                            className="p-1 text-slate-400 hover:text-slate-300 active:scale-95 cursor-pointer transition-transform"
                            title="Cancel"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ) : provider.isCustomOverride ? (
                      <div className="flex items-center space-x-1.5 bg-indigo-400/20 px-2 py-0.5 rounded-md border border-indigo-300/40">
                        <span className="text-[9.5px] font-bold uppercase tracking-wider text-indigo-300">
                          Custom:
                        </span>
                        <div className="font-mono text-[10.5px] text-white">
                          {provider.hasCustomTreatment && <span>Tx: ${summary.ratePerSession}/sess </span>}
                          {provider.hasCustomEval && <span>Eval: ${summary.ratePerEval}/eval</span>}
                        </div>
                        {userRole === 'ADMIN' && data?.payPeriod?.status !== 'FINALIZED' && (
                          <button
                            onClick={() => {
                              setEditingRateProviderId(provider.id);
                              setTempTreatmentRate(String(summary.ratePerSession || ''));
                              setTempEvalRate(String(summary.ratePerEval || ''));
                            }}
                            className="p-0.5 text-indigo-400 hover:text-indigo-200 active:scale-95 cursor-pointer transition-transform"
                            title="Edit custom rate overrides"
                          >
                            <Edit2 className="w-2.5 h-2.5" />
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-1.5 bg-white/12 px-2 py-0.5 rounded-md border border-white/25">
                        <span className="text-[9.5px] font-bold uppercase tracking-wider text-sky-200">
                          Standard:
                        </span>
                        <span className="text-slate-300 font-mono text-[10.5px]">
                          W1: <strong className="text-white">{summary.week1OnsiteTreatmentCount}</strong> (<strong className="text-sky-300">${summary.week1Rate}</strong>)
                        </span>
                        <span className="text-slate-400">|</span>
                        <span className="text-slate-300 font-mono text-[10.5px]">
                          W2: <strong className="text-white">{summary.week2OnsiteTreatmentCount}</strong> (<strong className="text-sky-300">${summary.week2Rate}</strong>)
                        </span>
                        {userRole === 'ADMIN' && data?.payPeriod?.status !== 'FINALIZED' && (
                          <button
                            onClick={() => {
                              setEditingRateProviderId(provider.id);
                              setTempTreatmentRate('');
                              setTempEvalRate('');
                            }}
                            className="p-0.5 text-slate-400 hover:text-sky-300 active:scale-95 cursor-pointer transition-colors"
                            title="Configure custom rate override"
                          >
                            <Edit2 className="w-2.5 h-2.5" />
                          </button>
                        )}
                      </div>
                    )}

                    {/* Completed Sessions */}
                    <div className="text-slate-100 bg-white/12 px-2 py-0.5 rounded-md border border-white/25 text-[10.5px]">
                      <span className="text-slate-200">Services: </span>
                      <strong className="text-white font-mono">{summary.completedServiceCount}</strong>
                    </div>

                    {/* Base Earned Pay */}
                    <div className="text-slate-100 bg-white/12 px-2 py-0.5 rounded-md border border-white/25 text-[10.5px]">
                      <span className="text-slate-200">Base: </span>
                      <strong className="text-white font-mono">
                        ${summary.totalProratedPay?.toFixed(2) ?? '0.00'}
                      </strong>
                    </div>

                    {/* Adjustments */}
                    {summary.totalManualAdjustments !== 0 && (
                      <div className="bg-amber-400/20 border border-amber-300/40 px-2 py-0.5 rounded-md text-amber-100 font-mono text-[10.5px]">
                        Adj: {summary.totalManualAdjustments > 0 ? '+' : ''}${summary.totalManualAdjustments?.toFixed(2)}
                      </div>
                    )}

                    {/* Calculated Total Pay */}
                    <div className="bg-emerald-400/25 border border-emerald-300/45 px-2.5 py-0.5 rounded-md text-emerald-100 font-bold font-mono text-xs">
                      ${summary.calculatedTotalPay?.toFixed(2) ?? '0.00'}
                    </div>
                  </div>
                </div>

                {/* Individual Completed Services Breakdown */}
                {isExpanded && (
                  <div className="border-t border-slate-200 bg-slate-50/60 p-2.5 space-y-2">
                    <div className="text-xs font-semibold text-slate-700 flex items-center justify-between px-0.5">
                      <span className="text-[11px] font-bold text-slate-800 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                        <span>Completed Service Records ({services.length})</span>
                      </span>
                      <span className="text-[10px] text-slate-600 font-mono hidden sm:inline">
                        Prorated automatically if actual differs from planned duration (treatment only)
                      </span>
                    </div>

                    {services.length === 0 ? (
                      <div className="py-3 text-center text-slate-600 text-xs bg-white rounded-md border border-slate-200">
                        No completed services rendered during this pay period.
                      </div>
                    ) : (
                      <>
                        <div className="hidden md:block overflow-x-auto rounded-md border border-slate-200 bg-white">
                          <table className="w-full text-left text-xs border-collapse">
                          <thead className="bg-[#162030] text-[9.5px] uppercase font-bold text-slate-200 border-b border-slate-700">
                            <tr>
                              <th className="px-2 py-1.5 font-bold whitespace-nowrap">DOS</th>
                              <th className="px-1.5 py-1.5 font-bold whitespace-nowrap">Week</th>
                              <th className="px-2 py-1.5 font-bold whitespace-nowrap">Client</th>
                              <th className="px-2 py-1.5 font-bold whitespace-nowrap">Type / Modality</th>
                              <th className="px-1.5 py-1.5 font-bold whitespace-nowrap">CPT</th>
                              <th className="px-1.5 py-1.5 font-bold whitespace-nowrap">Planned</th>
                              <th className="px-1.5 py-1.5 font-bold whitespace-nowrap">Actual</th>
                              <th className="px-2 py-1.5 font-bold whitespace-nowrap">Duration Status</th>
                              <th className="px-2 py-1.5 font-bold whitespace-nowrap">Base Rate</th>
                              <th className="px-2 py-1.5 font-bold whitespace-nowrap">Earned Pay</th>
                              <th className="px-2 py-1.5 font-bold whitespace-nowrap">Manual Adj.</th>
                              <th className="px-2 py-1.5 font-bold whitespace-nowrap">Calculated Total</th>
                              <th className="px-2 py-1.5 font-bold whitespace-nowrap">Status / Exception</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200 bg-white font-mono text-xs">
                            {services.map((srv: any, sIdx: number) => (
                              <tr
                                key={srv.id}
                                className={`group transition-colors duration-150 border-l-2 ${
                                  srv.isLateException
                                    ? sIdx % 2 === 0
                                      ? 'border-l-amber-500 bg-amber-50/70 hover:bg-amber-100/80 text-amber-950'
                                      : 'border-l-amber-500 bg-amber-50/40 hover:bg-amber-100/70 text-amber-950'
                                    : sIdx % 2 === 0
                                    ? 'border-l-transparent hover:border-l-cyan-600 bg-white hover:bg-slate-100/80 text-slate-900'
                                    : 'border-l-transparent hover:border-l-cyan-600 bg-slate-50/60 hover:bg-slate-100/80 text-slate-900'
                                }`}
                              >
                                {/* DOS */}
                                <td className="px-2 py-1 text-slate-800 whitespace-nowrap font-mono text-xs">
                                  {srv.dos}
                                  {srv.carriedFromEarlierPeriod && (
                                    <div className="mt-0.5 max-w-[150px] whitespace-normal font-sans text-[10px] leading-snug text-cyan-800">
                                      {srv.heldFromLabel || `Held from ${srv.heldFromPeriodLabel || 'an earlier period'}`}
                                    </div>
                                  )}
                                </td>

                                {/* Week */}
                                <td className="px-1.5 py-1 whitespace-nowrap">
                                  <span className={`px-1.5 py-0.2 rounded text-[9.5px] font-bold leading-none inline-block ${
                                    srv.weekNumber === 1
                                      ? 'bg-sky-50 text-sky-800 border border-sky-200'
                                      : 'bg-indigo-50 text-indigo-800 border border-indigo-200'
                                  }`}>
                                    W{srv.weekNumber}
                                  </span>
                                </td>

                                {/* Client (Make client strongest) */}
                                <td className="px-2 py-1 font-sans whitespace-nowrap">
                                  <div className="font-semibold text-slate-900 group-hover:text-cyan-700 text-xs leading-tight transition-colors">
                                    {srv.clientName}
                                  </div>
                                  {srv.ahcccsId && (
                                    <div className="font-mono text-[9.5px] text-slate-600 group-hover:text-slate-800 leading-none mt-0.5 transition-colors">
                                      AHCCCS: {srv.ahcccsId}
                                    </div>
                                  )}
                                </td>

                                {/* Type / Modality (Small) */}
                                <td className="px-2 py-1 whitespace-nowrap font-sans">
                                  <span className="text-[10.5px] font-medium text-slate-800 group-hover:text-slate-900 leading-tight block transition-colors">
                                    {srv.serviceType === 'EVALUATION' ? 'Evaluation' : 'Treatment'}
                                  </span>
                                  <span className="text-[9.5px] text-slate-600 leading-none">
                                    {srv.modality === 'TELETHERAPY' ? 'Teletherapy' : 'Onsite'}
                                  </span>
                                </td>

                                {/* CPT (Easy to scan) */}
                                <td className="px-1.5 py-1 whitespace-nowrap font-mono font-bold text-sky-700 text-xs">
                                  {srv.serviceCode}
                                </td>

                                {/* Planned (Tight) */}
                                <td className="px-1.5 py-1 whitespace-nowrap text-slate-700 font-mono text-xs">
                                  {srv.plannedDurationMinutes}m
                                </td>

                                {/* Actual (Tight) */}
                                <td className="px-1.5 py-1 whitespace-nowrap text-slate-900 font-bold font-mono text-xs">
                                  {srv.actualDurationMinutes}m
                                </td>

                                {/* Duration Status (Small pill) */}
                                <td className="px-2 py-1 whitespace-nowrap">
                                  {srv.rateSource === 'Owner non-billable — $25' ? (
                                    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 border border-slate-300 text-[9.5px] font-sans font-semibold leading-none inline-block" title={srv.billingDispositionReason || srv.rateSource}>
                                      Owner non-billable ($25)
                                    </span>
                                  ) : srv.rateSource === 'Owner documentation exception — $25' ? (
                                    <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-900 border border-sky-200 text-[9.5px] font-sans font-semibold leading-none inline-block" title={srv.ownerDocumentationExceptionReason || srv.rateSource}>
                                      Owner exception ($25)
                                    </span>
                                  ) : srv.rateSource?.startsWith('Compliance Rate') ? (
                                    <span className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-800 border border-rose-200 text-[9.5px] font-sans font-semibold leading-none inline-block">
                                      Compliance ($25)
                                    </span>
                                  ) : srv.serviceType === 'EVALUATION' ? (
                                    <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-800 border border-indigo-200 text-[9.5px] font-sans font-semibold leading-none inline-block">
                                      Flat Eval
                                    </span>
                                  ) : srv.isProrated ? (
                                    <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 text-[9.5px] font-sans font-semibold leading-none inline-block">
                                      Prorated ({Math.round((srv.actualDurationMinutes / srv.plannedDurationMinutes) * 100)}%)
                                    </span>
                                  ) : (
                                    <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 text-[9.5px] font-sans font-medium leading-none inline-block">
                                      Full (100%)
                                    </span>
                                  )}
                                </td>

                                {/* Base Rate */}
                                <td className="px-2 py-1 whitespace-nowrap">
                                  <div className="text-slate-800 font-mono text-xs leading-tight">
                                    ${srv.baseSessionRate?.toFixed(2) ?? '0.00'}
                                  </div>
                                  <div className="text-[9px] text-slate-600 font-sans leading-none truncate max-w-[110px]" title={srv.billingDispositionReason || srv.ownerDocumentationExceptionReason || srv.rateSource}>
                                    {srv.rateSource}
                                  </div>
                                  {srv.billingDispositionReason && (
                                    <div className="text-[9px] text-slate-700 font-sans leading-tight max-w-[140px]" title={srv.billingDispositionReason}>
                                      {srv.billingDispositionReason}
                                    </div>
                                  )}
                                  {srv.ownerDocumentationExceptionReason && (
                                    <div className="text-[9px] text-sky-800 font-sans leading-tight max-w-[140px]" title={srv.ownerDocumentationExceptionReason}>
                                      {srv.ownerDocumentationExceptionReason}
                                    </div>
                                  )}
                                  {userRole === 'OWNER' && data?.payPeriod?.status !== 'FINALIZED' && srv.rateSource !== 'Owner documentation exception — $25' && srv.rateSource !== 'Owner non-billable — $25' && (
                                    exceptionServiceId === srv.id ? (
                                      <div className="mt-1 space-y-1">
                                        <textarea
                                          value={exceptionReason}
                                          onChange={(e) => setExceptionReason(e.target.value)}
                                          rows={2}
                                          placeholder="Reason required"
                                          className="w-36 bg-white border border-sky-400 text-slate-900 px-1 py-0.5 rounded text-[10px]"
                                        />
                                        <div className="flex items-center space-x-1">
                                          <button
                                            type="button"
                                            onClick={() => handleSaveOwnerException(srv.id)}
                                            className="px-1 py-0.5 rounded bg-sky-700 text-white text-[9px] font-bold"
                                          >
                                            Set $25
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => setExceptionServiceId(null)}
                                            className="px-1 py-0.5 rounded text-slate-500 text-[9px]"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setExceptionServiceId(srv.id);
                                          setExceptionReason('');
                                        }}
                                        className="mt-1 text-[9px] text-sky-800 font-semibold underline"
                                      >
                                        Owner documentation exception — $25
                                      </button>
                                    )
                                  )}
                                </td>

                                {/* Earned Pay */}
                                <td className="px-2 py-1 text-slate-900 font-bold whitespace-nowrap">
                                  {srv.isLateException ? (
                                    <span className="text-amber-800 font-medium text-xs" title={srv.lateExceptionReason}>
                                      Pending Review
                                    </span>
                                  ) : (
                                    <div className="flex items-center space-x-1">
                                      <span>${srv.proratedPay?.toFixed(2) ?? '0.00'}</span>
                                      {srv.documentationEligible === false && (
                                        <span className="text-[8.5px] px-1 py-0.2 rounded bg-cyan-50 text-cyan-800 border border-cyan-300 uppercase font-sans font-medium leading-none" title={srv.documentationHoldReason || 'Not included in payable total'}>
                                          not included
                                        </span>
                                      )}
                                      {srv.isFrozen && (
                                        <span className="text-[8.5px] px-1 py-0.2 rounded bg-emerald-50 text-emerald-800 border border-emerald-300 uppercase font-sans font-medium leading-none" title="Locked from finalized payroll">
                                          frozen
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </td>

                                {/* Manual Adjustment (Compact) */}
                                <td className="px-2 py-1 whitespace-nowrap">
                                  {editingAdjustmentId === srv.id ? (
                                    <div className="flex items-center space-x-1">
                                      <input
                                        type="number"
                                        value={tempAdjustmentValue}
                                        onChange={(e) => setTempAdjustmentValue(e.target.value)}
                                        className="w-14 bg-white border border-amber-500 text-slate-900 px-1 py-0.5 rounded text-xs font-mono"
                                      />
                                      <button
                                        onClick={() => handleSaveAdjustment(srv.id)}
                                        className="p-0.5 text-emerald-600 hover:text-emerald-700 active:scale-95 cursor-pointer transition-transform"
                                        title="Save adjustment"
                                      >
                                        <Check className="w-3 h-3" />
                                      </button>
                                      <button
                                        onClick={() => setEditingAdjustmentId(null)}
                                        className="p-0.5 text-slate-400 hover:text-slate-600 active:scale-95 cursor-pointer transition-transform"
                                        title="Cancel"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center space-x-1">
                                      <span className={srv.manualAdjustment > 0 ? 'text-amber-800 font-bold text-xs' : srv.manualAdjustment < 0 ? 'text-rose-700 font-bold text-xs' : 'text-slate-600 text-xs'}>
                                        {srv.manualAdjustment > 0 ? `+$${srv.manualAdjustment.toFixed(2)}` : srv.manualAdjustment < 0 ? `-$${Math.abs(srv.manualAdjustment).toFixed(2)}` : '$0.00'}
                                      </span>
                                      {userRole !== 'ADMIN' && userRole !== 'SUPERVISOR' && data?.payPeriod?.status !== 'FINALIZED' && (
                                        <button
                                          onClick={() => {
                                            setEditingAdjustmentId(srv.id);
                                            setTempAdjustmentValue(String(srv.manualAdjustment || 0));
                                          }}
                                          className="p-0.5 text-slate-400 hover:text-amber-600 active:scale-95 cursor-pointer transition-all"
                                          title="Add/edit manual adjustment"
                                        >
                                          <Edit2 className="w-2.5 h-2.5" />
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </td>

                                {/* Calculated Total */}
                                <td className="px-2 py-1 text-emerald-700 font-bold whitespace-nowrap text-xs">
                                  {srv.documentationEligible === false ? (
                                    <span className="text-cyan-800 font-medium text-xs" title={srv.documentationHoldReason || 'Not included in payable total'}>Excluded</span>
                                  ) : srv.isLateException ? (
                                    <span className="text-amber-800 font-medium text-xs">Pending</span>
                                  ) : (
                                    `$${srv.calculatedTotal?.toFixed(2) ?? '0.00'}`
                                  )}
                                </td>

                                {/* Status / Exception (Exception text secondary) */}
                                <td className="px-2 py-1 font-sans whitespace-nowrap max-w-[200px]">
                                  {srv.isLateException ? (
                                    <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 text-[9px] font-bold leading-none inline-block truncate max-w-[180px]" title={srv.lateExceptionReason}>
                                      LATE SERVICE REVIEW
                                    </span>
                                  ) : srv.documentationEligible === false ? (
                                    <span className="px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-800 border border-cyan-200 text-[9px] font-bold leading-none inline-block truncate max-w-[180px]" title={srv.documentationHoldReason || srv.clearanceLabel}>
                                      {srv.clearanceLabel || 'DOCUMENTATION PENDING'}
                                    </span>
                                  ) : srv.isAmbiguous ? (
                                    <span className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-800 border border-rose-200 text-[9px] font-bold leading-none inline-block" title={srv.ambiguityReason}>
                                      HOLD: AMBIGUOUS
                                    </span>
                                  ) : srv.isFrozen ? (
                                    <span className="text-emerald-700 text-[10px] flex items-center space-x-1">
                                      <Lock className="w-2.5 h-2.5" />
                                      <span>Frozen</span>
                                    </span>
                                  ) : (
                                    <span className="text-slate-600 text-[10px] truncate block" title={srv.completionNotes || ''}>
                                      {srv.completionNotes || 'Normal'}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* MOBILE STACKED SERVICE CARDS */}
                      <div className="block md:hidden space-y-2">
                        {services.map((srv: any, sIdx: number) => (
                          <div
                            key={`mb-srv-${srv.id}`}
                            className={`p-3 rounded-lg border text-xs space-y-2 transition-colors ${
                              srv.isLateException
                                ? 'border-amber-300 border-l-[3px] border-l-amber-500 bg-amber-50/60 text-slate-900'
                                : sIdx % 2 === 0
                                ? 'bg-white border-slate-200 text-slate-900 hover:border-slate-300 hover:bg-slate-50/80'
                                : 'bg-slate-50/70 border-slate-200 text-slate-900 hover:border-slate-300 hover:bg-slate-100/70'
                            }`}
                          >
                            {/* Header: Client name, DOS, Week */}
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="font-semibold text-slate-900 text-xs">
                                  {srv.clientName}
                                </div>
                                <div className="text-[10px] font-mono text-slate-600 mt-0.5">
                                  {srv.dos} • CPT <span className="text-sky-700 font-bold">{srv.serviceCode}</span>
                                  {srv.carriedFromEarlierPeriod && (
                                    <div className="font-sans text-cyan-800">{srv.heldFromLabel || `Held from ${srv.heldFromPeriodLabel || 'an earlier period'}`}</div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                                  srv.weekNumber === 1
                                    ? 'bg-sky-50 text-sky-800 border border-sky-200'
                                    : 'bg-indigo-50 text-indigo-800 border border-indigo-200'
                                }`}>
                                  W{srv.weekNumber}
                                </span>
                                {srv.isLateException ? (
                                  <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 text-[9px] font-bold">
                                    Late
                                  </span>
                                ) : srv.isAmbiguous ? (
                                  <span className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-800 border border-rose-200 text-[9px] font-bold">
                                    Hold
                                  </span>
                                ) : null}
                              </div>
                            </div>

                            {/* Service Type & Duration */}
                            <div className="flex items-center justify-between text-[11px] pt-1 border-t border-slate-200 font-sans">
                              <span className="text-slate-700">
                                {srv.serviceType === 'EVALUATION' ? 'Evaluation' : 'Treatment'} ({srv.modality === 'TELETHERAPY' ? 'Tele' : 'Onsite'})
                              </span>
                              <div className="flex items-center gap-1.5 font-mono">
                                <span className="text-slate-600">{srv.plannedDurationMinutes}m plan</span>
                                <span className="text-slate-900 font-bold">{srv.actualDurationMinutes}m actual</span>
                              </div>
                            </div>

                            {/* Compensation & Totals */}
                            <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-200 font-mono">
                              <div>
                                <span className="text-slate-600 text-[10px]">Earned: </span>
                                <span className="text-slate-900 font-bold">
                                  {srv.isLateException ? 'Pending' : `$${srv.proratedPay?.toFixed(2) ?? '0.00'}`}
                                  {srv.documentationEligible === false && (
                                    <span className="ml-1 text-[10px] font-sans font-semibold text-cyan-800">not included</span>
                                  )}
                                </span>
                                {srv.manualAdjustment !== 0 && (
                                  <span className={`ml-1 text-[10.5px] ${srv.manualAdjustment > 0 ? 'text-amber-800 font-semibold' : 'text-rose-700 font-semibold'}`}>
                                    ({srv.manualAdjustment > 0 ? `+${srv.manualAdjustment.toFixed(2)}` : srv.manualAdjustment.toFixed(2)})
                                  </span>
                                )}
                              </div>
                              <div className="text-right">
                                <span className="text-[10px] text-slate-600">Total: </span>
                                <span className="text-xs font-bold text-emerald-700">
                                  {srv.documentationEligible === false ? 'Excluded' : srv.isLateException ? 'Pending' : `$${srv.calculatedTotal?.toFixed(2) ?? '0.00'}`}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Finalize Payroll Confirmation Modal */}
      {showFinalizeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-lg w-full p-6 shadow-2xl space-y-4 max-h-[85vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-2">
                <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">Finalize Payroll</h3>
                  <p className="text-xs text-slate-400 font-mono">{selectedPeriod.label}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowFinalizeModal(false);
                  setFinalizeResult(null);
                }}
                disabled={finalizing}
                className="text-slate-400 hover:text-white p-1 rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {isBeforeCutoff ? (
              <div className="p-3.5 bg-rose-500/10 border border-rose-500/20 rounded-lg text-xs text-rose-300 space-y-1.5">
                <p className="font-bold flex items-center space-x-1.5 text-rose-200">
                  <AlertCircle className="w-4 h-4 text-rose-400" />
                  <span>Premature Finalization Blocked</span>
                </p>
                <p className="text-rose-200/90 text-[11px] leading-relaxed">
                  This pay period ends on <strong>{selectedPeriod.endDate}</strong> (Cutoff Friday). {averagingCutoff
                    ? 'Finalizing is available on Saturday, after that Friday is complete.'
                    : 'Allegiant policy forbids finalizing payroll before the end of the pay period.'}
                </p>
              </div>
            ) : finalizeResult ? (
              <div className="p-3.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-xs text-emerald-100 space-y-2">
                <p className="font-semibold text-emerald-200">Payroll finalized.</p>
                <p>{finalizeResult.finalizedServicesCount} visit{finalizeResult.finalizedServicesCount === 1 ? '' : 's'} locked into payable payroll.</p>
                {(finalizeResult.carriedServices || []).length > 0 && (
                  <div>
                    <p>{finalizeResult.carriedServices!.length} visit{finalizeResult.carriedServices!.length === 1 ? '' : 's'} held from an earlier period included at the original earned amount:</p>
                    <ul className="mt-1 space-y-1 text-[11px] text-emerald-50/90">
                      {finalizeResult.carriedServices!.map((item) => (
                        <li key={item.id}>
                          <span className="font-semibold">{item.clientName || item.id}</span>
                          {' '}{item.dos} {item.serviceCode} — {item.clearanceLabel || 'Held from an earlier period'}
                          {item.calculatedPay != null ? ` ($${Number(item.calculatedPay).toFixed(2)})` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p>{finalizeResult.excludedServices.length} visit{finalizeResult.excludedServices.length === 1 ? '' : 's'} remain pending because documentation is incomplete. Those visits were not included.</p>
                {finalizeResult.excludedServices.length > 0 && (
                  <ul className="space-y-1.5 text-[11px] text-emerald-50/90">
                    {finalizeResult.excludedServices.map((item) => (
                      <li key={item.id}>
                        <span className="font-semibold">{item.clientName || item.id}</span>
                        {' '}{item.dos} {item.serviceCode} — {item.clearanceLabel || 'Documentation pending'}
                        {item.calculatedPay != null ? ` (calculated $${Number(item.calculatedPay).toFixed(2)}, not included)` : ''}
                        {item.reasons && item.reasons.length > 0 ? `: ${item.reasons.join(' ')}` : ''}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : finalizeError ? (
              <div className="p-3.5 bg-rose-500/10 border border-rose-500/20 rounded-lg text-xs text-rose-200">
                {finalizeError}
              </div>
            ) : (
              <div className="p-3.5 bg-amber-500/10 border border-amber-500/20 rounded-lg text-xs text-amber-300 space-y-1.5">
                <p className="font-semibold">Atomic Historical Freeze Notice:</p>
                <ul className="list-disc list-inside text-amber-200/90 space-y-1 text-[11px]">
                  <li>Freezes earned pay for documentation-complete visits in this pay period.</li>
                  <li>Incomplete documentation stays visible and is excluded from this payable total. After this period is finalized, completing that documentation assigns the visit to the open payroll period. That assignment stays in place until the assigned period is finalized.</li>
                  <li>Freezes rate tiers, proration factors, and snapshot metadata in PostgreSQL.</li>
                  <li>Any encounters entered or backdated after finalization will be treated as late exceptions.</li>
                  <li>This operation is authoritative and immutable. Already finalized periods are not recalculated.</li>
                </ul>
                {carriedInclusions.length > 0 && (
                  <div className="pt-1 text-amber-100">
                    <p className="font-semibold">{carriedInclusions.length} visit{carriedInclusions.length === 1 ? '' : 's'} held from an earlier period will be included at the original earned amount:</p>
                    <ul className="mt-1 space-y-1 text-[11px]">
                      {carriedInclusions.map((item: any) => (
                        <li key={item.id}>
                          {item.providerName ? `${item.providerName} — ` : ''}{item.clientName} {item.dos} {item.serviceCode}: held from {item.heldFromPeriodLabel || 'an earlier period'}
                          {item.calculatedPay != null ? ` ($${Number(item.calculatedPay).toFixed(2)})` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {documentationExclusions.length > 0 && (
                  <div className="pt-1 text-amber-100">
                    <p className="font-semibold">{documentationExclusions.length} visit{documentationExclusions.length === 1 ? '' : 's'} will be excluded:</p>
                    <ul className="mt-1 space-y-1 text-[11px]">
                      {documentationExclusions.map((item) => (
                        <li key={item.id}>
                          {item.providerName ? `${item.providerName} — ` : ''}{item.clientName} {item.dos} {item.serviceCode}: {item.clearanceLabel || item.reasons[0] || 'Documentation pending'}
                          {item.calculatedPay != null ? ` (calculated $${Number(item.calculatedPay).toFixed(2)})` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {!isBeforeCutoff && !finalizeResult && (
              <div className="space-y-1.5">
                <label className="text-xs text-slate-300 font-medium">Finalization Notes (optional):</label>
                <textarea
                  value={finalizeNotes}
                  onChange={(e) => setFinalizeNotes(e.target.value)}
                  placeholder="e.g., Verified against Tuesday payroll run. Exported to Gusto."
                  rows={2}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2.5 text-xs text-slate-200 focus:outline-none focus:border-emerald-500 font-sans"
                />
              </div>
            )}

            <div className="flex items-center justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowFinalizeModal(false);
                  setFinalizeResult(null);
                }}
                disabled={finalizing}
                className="px-4 py-2 rounded-lg border border-slate-700 hover:bg-white/5 text-xs text-slate-300 font-medium"
              >
                {finalizeResult ? 'Close' : 'Cancel'}
              </button>
              {!isBeforeCutoff && !finalizeResult && (
                <button
                  type="button"
                  onClick={handleFinalizePayroll}
                  disabled={finalizing}
                  className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold flex items-center space-x-1.5 shadow-md disabled:opacity-50 cursor-pointer"
                >
                  <ShieldCheck className="w-4 h-4" />
                  <span>{finalizing ? 'Finalizing in Database...' : 'Confirm & Finalize Payroll'}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
